import { AppDataContext } from "modules/main/context/appData";
import { useCart } from "modules/cart/hooks/useCart";
import { useFetch } from "modules/main/hooks/useFetch";
import { useCallback, useContext, useEffect, useState} from "react";
import { endPointOptimizedResults } from "modules/main/services/endpoints";
import { OptimizedCartResponse, CartCreationStatus, CartItemStatus } from "modules/checkout/types/optimizedCart";
import { FetchDataOptions} from "modules/main/services/fetchData";

import { CartTableDetail } from "modules/checkout/components/cartDetailTable";
import { CartTableResume } from "modules/checkout/components/cartResumeTable";
import Header from "modules/main/components/header";
import { AuthError } from "modules/authentication/types/form-login-types";
import { NoAuthError } from "modules/authentication/components/no-auth-error";
import { GeneralError } from "modules/main/components/general-error";
import { isDesktopSize } from "utils/is-desktop";
import { useMediaQuery } from "@/hooks/use-media-query";


import { CartItem } from "modules/cart/types/cart";
import { useOptimizedCartUpdates } from "../hooks/useOptimizedScenarioUpdate";
import { getTotalsFromScenario } from "utils/scenarioUtils";

import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from "@/components/ui/empty"
import { ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { ViewOrderSkeleton } from "modules/checkout/components/viewOrderSkeleton";
import posthog from "posthog-js";
import { usePageTitle } from "hooks/usePageTitle";


// const EXTENSION_ID = "pdnphkigpecdigolfkopmbhejkkopcdn"; // Carry Checkout Extension ID
// declare const chrome: any;
const CREATE_CART_EVENT_CODE = "Crear carrito";

export const ViewOrder = () => {
    const {requireLoginAction} = useAuth();
    const {setTitle,setShowBack } = usePageTitle();
    const {cart, clearCart} = useCart();    
    const navigate = useNavigate();   
    const [recalculate, setRecalculate] = useState(false);    
    const {storeList} = useContext(AppDataContext); 
    const isDesktop = useMediaQuery(isDesktopSize);
    const [refresh, setRefresh] = useState(false);

    const [workingCart, setWorkingCart] = useState<CartItem[]>([]);    
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const requestOptions:FetchDataOptions = {
        method:"GET",
        headers:{
            "content-type":"application/json",
            // ...(!isLogged ? { "x-visitor-id": getVisitorId() } : {})
        }
    }

    const { order_id: orderIdParam, scenario_id: scenarioIdParam, store_id: storeIdParam } = useParams<{ order_id: string; scenario_id: string; store_id?: string }>();
    const order_id = Number.isFinite(Number(orderIdParam)) ? Number(orderIdParam) : 0;
    const scenario_id = Number.isFinite(Number(scenarioIdParam)) ? Number(scenarioIdParam) : 0;
    const store_id = Number.isFinite(Number(storeIdParam)) ? Number(storeIdParam) : undefined;

    const [cartStatus, setCartStatus] = useState<CartCreationStatus[]>([]);
    // console.log("Params:", requestOptions);
    const {data,error,loading} = useFetch<OptimizedCartResponse>(endPointOptimizedResults(order_id, scenario_id, store_id),requestOptions,refresh);

    const [scenarios, setScenarios] = useState<OptimizedCartResponse["scenarios"]>([]);

    const [selectedIndexScenario, setSelectedIndexScenario] = useState(0);

    
    const { applyChanges: applyCartChanges, isApplyingChanges } = useOptimizedCartUpdates({
            orderId: order_id,
            selectedIndexScenario,
            onSuccess: (updatedData) => {
                setHasUnsavedChanges(false);
                // Actualizar workingCart con datos del servidor
                const serverCart = updatedData.scenarios[selectedIndexScenario].opt_cart.map(item => ({
                    ...item,
                    price_component: item.price_component.map(pc => ({...pc}))
                }));
                setWorkingCart(serverCart);
                setScenarios(updatedData.scenarios);
            },
            onError: (error) => {
                console.warn("Error aplicando cambios:", error.message);
                setHasUnsavedChanges(true);
            }
    });

    const applyChanges = () => applyCartChanges(scenarios[selectedIndexScenario].order_id??0, workingCart);
    
    const initializeWorkingArea = (scenarioIndex:number,scenariosData?: OptimizedCartResponse["scenarios"]) =>{
        const currentScenarios = scenariosData || scenarios;
        //Clone cart 
        if (currentScenarios && currentScenarios[scenarioIndex]) {
            const clonedCart = currentScenarios[scenarioIndex].opt_cart.map(item => ({
                ...item,
                price_component: item.price_component.map(pc => ({...pc}))
            }));
            setWorkingCart(clonedCart);
            setHasUnsavedChanges(false);
        }
    }

    const initializeCartStatus = useCallback((scenarioIndex: number, scenariosData?: OptimizedCartResponse["scenarios"]) => {
        const currentScenarios = scenariosData || scenarios;
        if (!currentScenarios || !currentScenarios[scenarioIndex]) return;

        const currentScenario = currentScenarios[scenarioIndex];
        const isUniqueStore = currentScenario.scenarioType === "UNIQUE_STORE";
        const { cheapestStoreId } = getTotalsFromScenario(currentScenario, isUniqueStore, false);

        const newCartStatus: CartCreationStatus[] = currentScenario.store_resume
                .filter(store=>((cheapestStoreId === store.id && isUniqueStore) || !isUniqueStore))
                .map(store => {
                    const productsForStore: CartItemStatus[] = currentScenario.opt_cart.reduce((acc, item) => {
                        const storeComponent = item.price_component.find(pc => pc.id === store.id && !pc.no_met_conditions);
                        if (storeComponent && storeComponent.quantity > 0) {
                            acc.push({
                                ...item,
                                price_component: item.price_component.map(pc => ({
                                    ...pc,
                                    checked: false
                                }))
                            });
                        }
                        return acc;
                    }, [] as CartItemStatus[]);

                    return {
                        store_id: store.id,
                        products: productsForStore
                    };
                });
                setCartStatus(newCartStatus);
    }, [scenarios]);
 


    useEffect(() => {
    if (data?.scenarios && data.scenarios.length > 0) {
        // Solo inicializar una vez al cargar los datos
        setScenarios(data.scenarios);
        const scenarioIndexFromPath = data.scenarios.findIndex((scenario) => scenario.order_id === scenario_id);
        const reccomendedIndex = Math.max(0, data.scenarios.findIndex((scenario) => scenario.reccomended));
        const initialIndex = scenarioIndexFromPath >= 0 ? scenarioIndexFromPath : reccomendedIndex;
        setSelectedIndexScenario(initialIndex);
        if(workingCart.length === 0){
            initializeWorkingArea(initialIndex, data.scenarios);
            initializeCartStatus(initialIndex, data.scenarios);
        }
        
    }
    }, [data, initializeCartStatus, scenario_id]);

    useEffect(() => {
      setTitle("Detalle de la optimización");
      setShowBack(true);
    }, [setTitle]);

    const handleScenarioChange = useCallback((index: number, loading: boolean) => {
        
        if (!scenarios?.[index] || loading) return;
        
        const clonedCart = scenarios[index].opt_cart.map(item => ({
            ...item,
            price_component: item.price_component.map(pc => ({...pc}))
        }));
        setSelectedIndexScenario(index);
        setWorkingCart(clonedCart);
        setHasUnsavedChanges(false);
        initializeCartStatus(index);
    }, [scenarios, initializeCartStatus]);

    
    // Función para descartar cambios
    const resetWorkingCart = () => {
        initializeWorkingArea(selectedIndexScenario);
    };


    const onCreateCart = (scenarioId:number, storeId?:number) => {
        
        if(!requireLoginAction()){            
            return;
        }
        posthog.capture(CREATE_CART_EVENT_CODE);
        //navigate to details page with scenarioId as parameter
        navigate('/cart/create_cart?scenario_id=' + scenarioId + (storeId ? `&store_id=${storeId}` : ""));
    }


    if(loading){
        return <ViewOrderSkeleton isDesktop={isDesktop} />;
    }

    if(error){
        const ErrorComponent = (error instanceof AuthError) ? NoAuthError : GeneralError;
        return (<><ErrorComponent/></>);
    }

    if(!data){
        return <ViewOrderSkeleton isDesktop={isDesktop} />;
    }
    
    if(scenarios.length === 0){
        return (<>
        <div className="w-full flex justify-center items-center min-h-[50dvh]">
            <Empty>
                <EmptyHeader>
                    <EmptyMedia variant="icon">
                    <ShoppingCart />
                    </EmptyMedia>
                    <EmptyTitle>Tu carrito aún es muy pequeño</EmptyTitle>
                    <EmptyDescription>
                    Todavía no alcanzas el monto mínimo para optimizar precios entre tiendas.
                    Agrega más productos y podrás ver cómo se distribuye tu compra para ahorrar.
                    </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                    <div className="flex gap-2">
                    <Link to="/">
                        <Button>Agregar más productos</Button>
                    </Link>                
                    </div>
                </EmptyContent>
            </Empty>
        </div>
        </>
    );
    }
    const tableResume = (
        <CartTableResume 
            mode = "RESULT"
            // alert={data?.alert??false}
            // selectedScenario={scenarios[selectedIndexScenario]??emptyScenario}
            setSelectedIndexScenario={handleScenarioChange}
            selectedIndexScenario={selectedIndexScenario}
            optimizedScenarios={scenarios}
            storeId={store_id}
            currCart={cart}
            storeList={storeList}
            className="overflow-hidden min-w-[25rem]"
            isMobile={!isDesktop}
            loading={isApplyingChanges}
            refreshTrigger={()=>setRefresh(!refresh)}
            clearCart={clearCart}
            recalculate={recalculate}
            setRecalculate={setRecalculate}
            cartCreationStatus={cartStatus}
    />
    );
    
    return (
    <>    
    <div className="py-4 px-6">
        <div className={cn("flex w-full flex-col gap-2",{
            "w-full":!isDesktop,
            "lg:w-[80%] xl:w-[80%] 2xl:w-[60%] mx-auto":isDesktop,
        })}>
            <CartTableDetail 
                mode = "RESULT"
                selectedIndexScenario={selectedIndexScenario}
                // alert={data?.alert??false}
                optimizedScenarios={scenarios}
                loading={isApplyingChanges}
                // status = {data?.success}
                errorCode={data?.error}
                currCart={cart}
                createCartCallBack={onCreateCart}
                addDeletefromCart={()=>{            
                }}
                setNoReplace ={(productId, newMethod) => {
                    // Actualizar el workingCart con el nuevo método
                    const updatedCart = workingCart.map(item => 
                        item.id === productId 
                            ? { ...item, method: newMethod }
                            : item
                    );
                    setWorkingCart(updatedCart);
                    setHasUnsavedChanges(true); // Marcar como cambios no guardados
                }}
                storeList={storeList}       
                className="flex-1"
                isMobile={!isDesktop}
                setSelectedIndexScenario={handleScenarioChange}
                workingCart={workingCart}
                setWorkingCart={setWorkingCart}
                hasUnsavedChanges={hasUnsavedChanges}
                applyChanges={applyChanges}
                resetWorkingCart={resetWorkingCart}       
                cartCreationStatus={cartStatus}     
                setCartStatus={setCartStatus}
            />
        </div>

    </div>
    {(!isDesktop)&&tableResume}
    </>
    );

}

// const Instructions:React.FC<{installed:boolean|null}> = ({installed}) => {

//     const handleClick = async () => {
//         if (!chrome?.runtime?.sendMessage) {
//             console.error("La API de Chrome no está disponible.");
//             return;
//         }

//         await chrome.runtime.sendMessage(
//             EXTENSION_ID,
//             { type: "open_side_panel" },
//             (response:any) => {
//                 if (chrome.runtime.lastError) {
//                     console.error("Error al enviar mensaje:", chrome.runtime.lastError);
//                     return;
//                 }
//             }
//         );
//     };
//     return (
//         <Card className="lg:w-[60rem]">
//             <CardHeader className="flex flex-row items-start bg-muted/50">
//                 <div className="grid gap-0.5">
//                     <CardTitle className="flex flex-row items-start gap-2">
//                         <Bell className="w-4 h-4 mb-2"/><span>Finaliza tu compra</span>
//                     </CardTitle>
//                     <CardDescription>Sigue estas instrucciones para terminar tu pedido</CardDescription>                    
//                 </div>
//             </CardHeader>                    
//             <CardContent className="text-sm p-4">       
//                 <div className="flex flex-col gap-2">
//                     <div className="font-semibold flex flex-row gap-4 items-center"><MainLogo classname="w-6" />¡Ya casi terminas!</div>
//                     <p className=" mt-4">A tu derecha se abrirá la <span className="font-semibold text-foreground">extensión de Carri</span>, donde <span className="font-semibold">podrás completar tu compra</span> fácilmente.
//                     Desde allí,<span className="mr-1 font-semibold"> podrás acceder rápidamente a los productos en cada supermercado</span>, armar tu carrito y <span className="font-semibold">pagar directamente en cada tienda</span>.
//                     </p>
//                 {(!installed)?(
//                     <p>
//                         ¿Aún no la tienes? Haz clic en   <Button className="m-auto" variant={"default"}>
//                     <a href="https://chromewebstore.google.com/detail/carry-checkout/pdnphkigpecdigolfkopmbhejkkopcdn?authuser=0&hl=es-419&pli=1" target="_blank" rel="noopener noreferrer">
//                         <div className="flex gap-2">
//                             <Blocks className="w-8 h-8"/> Instalar Extensión
//                         </div>
//                     </a>
//                     </Button>  para empezar / Recuerda es sólo para <span className="font-semibold">Chrome</span>.
//                     </p>
//                 ):(
//                     <p className="mt-8">                                         
//                         Si la extensión no se abre automáticamente, haz clic en 
//                         <Button className="ml-2" variant={"default"} onClick={handleClick} id="carry-call-extension">                    
//                             <div className="flex gap-2">
//                                 <Blocks className="w-8 h-8"/> Abrir Extensión
//                             </div>
//                         </Button>                         
//                     </p>
//                 )}
//                 <p>
//                     Esta optización podrá ser consultada cuando quieras en <Link to="settings/orders"><span className="font-semibold">"Mis Órdenes"</span></Link> en los datos de tu cuenta.
//                 </p>
//                 </div>                                                   
//             </CardContent>
//             <CardFooter>
//             </CardFooter>
//         </Card>       
//     );
// }
